import type {RouteRecordRaw} from "vue-router";

interface RouteMetaType {
    title?: string;
    requiresAuth?: boolean // add this if authentication is needed
    layout?: string; // add this if a specific layout is needed
}

export const allRoutes: Array<RouteRecordRaw & { meta?: RouteMetaType }> = [
    {
        path: '/',
        redirect: '/home'
    },
    {
        path: '/home',
        name: 'landing',
        component: () => import('@/views/landing/index.vue'),
        meta: { layout: 'public' } // Use PublicLayout
    },
    {
        path: '/events',
        name: 'events',
        meta: {
            requiresAuth: false,
            layout: 'public'
        },
        component: () => import('@/views/pages/public-events.vue'),
    },
    {
        path: '/events/:id', // ✅ Dynamic route for event details
        name: 'events.details',
        meta: { requiresAuth: false, layout: 'public' },
        component: () => import('@/views/pages/events/eventDetails.vue'),
    },
    {
        // TODO maybe a generic profile page as well
        path: '/profiles/:id', // ✅ Dynamic route for event details
        name: 'profiles.details',
        meta: { requiresAuth: false, layout: 'public' },
        component: () => import('@/views/pages/profiles/announcers/announcerDetails.vue'),
    },
    {
        path: '/announcers',
        name: 'announcers',
        meta: {
            requiresAuth: false,
            layout: 'public'
        },
        component: () => import('@/views/pages/public-announcers.vue'),
    },
    {
        path: '/announcers/:id', // ✅ Dynamic route for event details
        name: 'announcers.details',
        meta: { requiresAuth: false, layout: 'public' },
        component: () => import('@/views/pages/profiles/announcers/announcerDetails.vue'),
    },
    {
        path: '/providers',
        name: 'providers',
        meta: {
            requiresAuth: false,
            layout: 'public'
        },
        component: () => import('@/views/pages/public-providers.vue'),
    },
    {
        path: '/providers/:id', // ✅ Dynamic route for event details
        name: 'providers.details',
        meta: { requiresAuth: false, layout: 'public' },
        component: () => import('@/views/pages/profiles/providers/providerDetails.vue'),
    },
    {
        path: '/fans',
        name: 'fans',
        meta: {
            requiresAuth: false,
            layout: 'public'
        },
        component: () => import('@/views/pages/public-fans.vue'),
    },
    {
        path: '/fans/:id', // ✅ Dynamic route for event details
        name: 'fans.details',
        meta: { requiresAuth: false, layout: 'public' },
        component: () => import('@/views/pages/profiles/fans/fanDetails.vue'),
    },

    {
        path: '/partners',
        name: 'partners',
        meta: {
            requiresAuth: false,
            layout: 'public'
        },
        component: () => import('@/views/pages/partners.vue'),
    },
    {
        path: '/company',
        name: 'company',
        meta: {
            requiresAuth: false,
            layout: 'public'
        },
        component: () => import('@/views/pages/company.vue'),
    },

    {
        path: '/my/',
        children: [
            {
                path: 'home',
                name: 'my.index',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/index.vue')
            },
            {
                path: 'account',
                name: 'my.account',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/account.vue')
            },
            {
                path: 'profile',
                name: 'my.profile',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/profile.vue')
            },
            {
                path: 'profile/edit',
                name: 'my.profile.edit',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/profiles/profileEdit.vue'),
            },
            {
                path: 'events',
                name: 'my.events',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/events.vue'),
            },
            {
                path: 'events/new',
                name: 'my.events.create',
                meta: { requiresAuth: true, layout: 'private' },
                component: () => import('@/views/my/events/eventEdit.vue'),
            },
            {
                path: 'events/:id', // Dynamic route for event details
                name: 'my.events.details',
                meta: { requiresAuth: true, layout: 'private' },
                component: () => import('@/views/my/events/eventDetails.vue'),
            },

            {
                path: 'events/:id/edit',
                name: 'my.events.edit',
                meta: { requiresAuth: true, layout: 'private' },
                component: () => import('@/views/my/events/eventEdit.vue'),
            },

            {
                path: 'applications',
                name: 'my.applications',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/applications.vue'),
            },
            {
                path: 'applications/:id', // ✅ Dynamic route for event details
                name: 'my.applications.details',
                meta: { requiresAuth: true, layout: 'private' },
                component: () => import('@/views/my/applications/applicationDetails.vue'),
            },

            {
                path: 'assignments',
                name: 'my.assignments',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/assignments.vue'),
            },
            {
                path: 'assignments/:id', // ✅ Dynamic route for event details
                name: 'my.assignments.details',
                meta: { requiresAuth: true, layout: 'private' },
                component: () => import('@/views/my/assignments/assignmentDetails.vue'),
            },
            {
                path: 'assignments/:id/edit', // ✅ Dynamic route for event details
                name: 'my.assignments.edit',
                meta: { requiresAuth: true, layout: 'private' },
                component: () => import('@/views/my/assignments/assignmentEdit.vue'),
            },
            {
                path: 'viewer', // ✅ Dynamic route for event details
                name: 'my.viewer',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/viewer.vue'),
            },
            {
                path: 'viewer/:id', // ✅ Dynamic route for event details
                name: 'my.viewer.details',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/viewer/viewerDetails.vue'),
            },
            {
                path: 'favorites', // ✅ Dynamic route for event details
                name: 'my.favorites',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/favorites.vue'),
            },
            {
                path: 'help-center', // ✅ Dynamic route for event details
                name: 'my.help-center',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/help-center.vue'),
            },
            {
                path: 'dev-tools',
                name: 'my.dev.dev-tools',
                meta: {
                    requiresAuth: true,
                    layout: 'private'
                },
                component: () => import('@/views/my/dev/dev-tools.vue'),
            },
            {
                path: ':pathMatch(.*)*', // 404
                redirect: '/my/home'

            },
        ]
    },
    {
        path: '/pages',
        children: [
            {
                path: 'contact',
                name: 'pages.contact',
                meta: {
                    requiresAuth: false,
                    layout: 'public'
                },
                component: () => import('@/views/pages/static/contact.vue'),
            },
            {
                path: 'howto',
                name: 'pages.howto',
                meta: {
                    requiresAuth: false,
                    layout: 'public'
                },
                component: () => import('@/views/pages/static/howto.vue'),
            },
            {
                path: 'imprint',
                name: 'pages.imprint',
                meta: {
                    requiresAuth: false,
                    layout: 'public'
                },
                component: () => import('@/views/pages/static/imprint.vue'),
            },
            {
                path: 'terms',
                name: 'pages.terms',
                meta: {
                    requiresAuth: false,
                    layout: 'public'
                },
                component: () => import('@/views/pages/static/terms.vue'),
            },
            {
                path: 'privacy',
                name: 'pages.privacy',
                meta: {
                    requiresAuth: false,
                    layout: 'public'
                },
                component: () => import('@/views/pages/static/privacy.vue'),
            },
            {
                path: 'event',
                name: 'pages.event',
                meta: {
                    requiresAuth: false,
                    layout: 'public'
                },
                component: () => import('@/views/pages/static/dummy-event.vue'),
            },

            {
                path: 'test',
                name: 'pages.test-vuelidate',
                meta: {
                    requiresAuth: true,
                    layout: 'public'
                },
                component: () => import('@/views/pages/test/Vuelidate-Test.vue'),
            },
        ]
    },
    {
        path: '/auth/',
        children: [
            {
                path: 'sign-in',
                name: 'auth.sign-in',
                meta: {
                    title: 'Sign In'
                },
                component: () => import('@/views/auth/sign-in.vue'),
            },
            {
                path: 'sign-in-with-qr',
                name: 'auth.sign-in-with-qr',
                meta: {
                    title: 'Sign In With QR'
                },
                component: () => import('@/views/auth/sign-in-with-qr.vue'),
            },
            {
                path: 'sign-up',
                name: 'auth.sign-up',
                meta: {
                    title: 'Sign Up'
                },
                component: () => import('@/views/auth/sign-up.vue'),
            },
            {
                path: 'lock-screen',
                name: 'auth.lock-screen',
                meta: {
                    title: 'Lock Screen'
                },
                component: () => import('@/views/auth/lock-screen.vue'),
            },
            {
                path: 'forgot-password',
                name: 'auth.forgot-password',
                meta: {
                    title: 'Forgot Password'
                },
                component: () => import('@/views/auth/forgot-password.vue'),
            },
            {
                path: 'reset-password/:id/:token',
                name: 'auth.reset-password',
                meta: {
                    title: 'Reset Password'
                },
                component: () => import('@/views/auth/reset-password.vue'),
            },

            {
                path: 'logout',
                name: 'auth.logout',
                meta: {
                    title: 'Logout'
                },
                component: () => import('@/views/auth/logout.vue'),
            },
        ]
    },
    {
        path: '/internal/',
        children: [
            {
                path: 'how-to-support-anymic/:targetGroup',
                name: 'internal.support-anymic',
                meta: {
                    title: 'Support'
                },
                component: () => import('@/views/internal/how-to-support-anymic.vue'),
            }
            ]
    },

    // 404
    {
        path: '/:pathMatch(.*)*', // 404
        redirect: '/404'

    },
    {
        path: '/404', // 404
        name: 'not-found',
        component: () => import('@/views/pages/404.vue')
    }

];
